import {
  LinkService,
  ListService,
  ListType,
  QuoteService,
  RichEditor,
  StyleService,
} from '@frontapp/editor-html';
import {isEqual, isUndefined} from 'lodash';
import memoizeOne from 'memoize-one';
import React, {FC, useCallback, useMemo, useRef, useState, useSyncExternalStore} from 'react';
import {animationFrameScheduler, merge} from 'rxjs';
import {debounceTime, skip} from 'rxjs/operators';

import {KeyboardActionsEnum} from '../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventEntryPointsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {knownKeyRenderers} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcutFormatterHelpers';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {trySetLinkUrlFromSelectedText} from '../../../../helpers/editorHelpers';
import {MarkdownStyle} from '../../../common/editor/style/markdownStyle';
import {FloatingToolbar} from '../../../common/floatingBars/floatingToolbar';
import {FloatingToolbarButton} from '../../../common/floatingBars/floatingToolbarButton';
import {CommentComposerFormattingLinkEditor} from './commentComposerFormattingLinkEditor';
import {
  CommentFormattingOptionsEnum,
  trackFormattingEvent,
  trackManualFormattingEvent,
} from './commentComposerFormattingTracking';

/*
 * Constants.
 */

const spaceKey = knownKeyRenderers.space();
const bulletedListCustomKeyboardAction = <>- {spaceKey}</>;
const numberedListCustomKeyboardAction = <>1. {spaceKey}</>;
const blockquoteCustomKeyboardAction = <>&gt; {spaceKey}</>;

/*
 * Props.
 */

interface CommentComposerFormattingToolbarProps {
  conversationId: number | undefined;
  editor: RichEditor;
  onRequestClose: () => void;
}

/*
 * Component.
 */

export const CommentComposerFormattingToolbar: FC<CommentComposerFormattingToolbarProps> = (props) => {
  const {conversationId, editor, onRequestClose} = props;
  const {style: styleService, link: linkService, list: listService, quote: quoteService} = editor.services;
  const {isBold, isItalic, hasStrikethrough, isLink, isUnorderedList, isOrderedList, isQuoted} =
    useSelectedFormatting(styleService, linkService, listService, quoteService);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [setLastUsedLinkEntryPoint, trackLinkEvent] = useLinkEventTracking(conversationId);

  const onLinkUrlSelect: (arg: string | undefined) => void = (newUrl) => {
    linkService.setUrl(newUrl);

    // Request for the toolbar to be dismissed. Never go back to formatting buttons.
    onRequestClose();

    trackLinkEvent();
  };

  if (isEditingLink) {
    return (
      <CommentComposerFormattingLinkEditor
        shouldFocus
        initialUrl={linkService.url}
        onUrlSelect={onLinkUrlSelect}
        onRequestClose={onRequestClose}
      />
    );
  }

  const onBoldButtonClick: () => void = () => {
    styleService.applyStyle({isBold: !isBold});
    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.BOLD);
  };

  const onItalicButtonClick: () => void = () => {
    styleService.applyStyle({isItalic: !isItalic});
    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.ITALIC);
  };

  const onStrikethroughButtonClick: () => void = () => {
    styleService.applyStyle({hasStrikethrough: !hasStrikethrough});
    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.STRIKETHROUGH);
  };

  const onLinkButtonClick: (arg: EventEntryPointsEnum) => void = (entryPoint) => {
    setLastUsedLinkEntryPoint(entryPoint);

    if (trySetLinkUrlFromSelectedText(linkService)) {
      trackLinkEvent();
      return;
    }

    setIsEditingLink(true);
  };

  const onUnorderedListButtonClick: () => void = () => {
    listService.setType(isUnorderedList ? undefined : ListType.UNORDERED);
    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.UNORDERED_LIST);
  };

  const onOrderedListButtonClick: () => void = () => {
    listService.setType(isOrderedList ? undefined : ListType.ORDERED);
    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.ORDERED_LIST);
  };

  const onBlockquoteButtonCLick: () => void = () => {
    if (isQuoted) {
      quoteService.unquote();
    } else {
      quoteService.quote();
    }

    trackManualFormattingEvent(conversationId, CommentFormattingOptionsEnum.BLOCKQUOTE);
  };

  // Other keyboard shortcuts (bold & italic) are handled by the editor itself.
  const keyboardShortcutHandlers = {
    [KeyboardActionsEnum.EDITOR_INSERT_LINK]: () => onLinkButtonClick(EventEntryPointsEnum.KEYBOARD_SHORTCUT),
  };

  return (
    <FloatingToolbar>
      <KeyboardShortcuts handlers={keyboardShortcutHandlers} />
      <FloatingToolbarButton
        iconName="formattingBoldSmall"
        label={<Trans id="formatting-bold">Bold</Trans>}
        keyboardAction={KeyboardActionsEnum.EDITOR_BOLD}
        isActive={isBold}
        onClick={onBoldButtonClick}
      />
      <FloatingToolbarButton
        iconName="formattingItalicSmall"
        label={<Trans id="formatting-italic">Italic</Trans>}
        keyboardAction={KeyboardActionsEnum.EDITOR_ITALIC}
        isActive={isItalic}
        onClick={onItalicButtonClick}
      />
      <FloatingToolbarButton
        iconName="formattingStrikethroughSmall"
        label={<Trans id="formatting-strikethrough">Strikethrough</Trans>}
        isActive={hasStrikethrough}
        onClick={onStrikethroughButtonClick}
      />
      <FloatingToolbarButton
        iconName="formattingLinkSmall"
        label={renderLinkButtonLabel(Boolean(isLink))}
        keyboardAction={KeyboardActionsEnum.EDITOR_INSERT_LINK}
        isActive={isLink}
        onClick={() => onLinkButtonClick(EventEntryPointsEnum.MANUAL)}
      />
      <FloatingToolbarButton
        iconName="formattingListSmall"
        label={<Trans id="formatting-bulletedlist">Bulleted list</Trans>}
        customKeyboardAction={bulletedListCustomKeyboardAction}
        isActive={isUnorderedList}
        onClick={onUnorderedListButtonClick}
      />
      <FloatingToolbarButton
        iconName="formattingNumberedListSmall"
        label={<Trans id="formatting-numberedlist">Numbered list</Trans>}
        customKeyboardAction={numberedListCustomKeyboardAction}
        isActive={isOrderedList}
        onClick={onOrderedListButtonClick}
      />
      <FloatingToolbarButton
        iconName="formattingBlockquoteSmall"
        label={<Trans id="formatting-blockquote">Quote</Trans>}
        customKeyboardAction={blockquoteCustomKeyboardAction}
        isActive={isQuoted}
        onClick={onBlockquoteButtonCLick}
      />
    </FloatingToolbar>
  );
};

function renderLinkButtonLabel(isLink: boolean) {
  return isLink ? (
    <Trans id="formatting-link-edit">Edit link</Trans>
  ) : (
    <Trans id="formatting-link">Insert link</Trans>
  );
}

/*
 * Helpers.
 */

function useSelectedFormatting(
  styleService: StyleService,
  linkService: LinkService,
  listService: ListService,
  quoteService: QuoteService,
): MarkdownStyle {
  const subscribe = useCallback(
    (callback: () => void) => {
      const subscription = merge(styleService.changes$, listService.changes$, quoteService.changes$)
        .pipe(skip(1), debounceTime(0, animationFrameScheduler))
        .subscribe(callback);

      return () => subscription.unsubscribe();
    },
    [listService.changes$, quoteService.changes$, styleService.changes$],
  );

  // We must return the same snapshot if the data in the "sync external store" has not changed.
  // https://react.dev/reference/react/useSyncExternalStore#im-getting-an-error-the-result-of-getsnapshot-should-be-cached
  const stabilizeSnapshot = useMemo(
    () =>
      memoizeOne(
        (style: MarkdownStyle) => style,
        ([prevStyle], [nextStyle]) => isEqual(prevStyle, nextStyle),
      ),
    [],
  );

  const getSnapshot = () => {
    const {style} = styleService;
    const listType = listService.type;

    return stabilizeSnapshot({
      isBold: style.isBold,
      isItalic: style.isItalic,
      hasStrikethrough: style.hasStrikethrough,
      isLink: !isUndefined(linkService.url),
      isUnorderedList: listType === ListType.UNORDERED,
      isOrderedList: listType === ListType.ORDERED,
      isQuoted: quoteService.isQuoted,
    });
  };

  return useSyncExternalStore(subscribe, getSnapshot);
}

function useLinkEventTracking(conversationId: number | undefined) {
  const lastUsedEntryPointRef = useRef(EventEntryPointsEnum.MANUAL);

  const setLastUsedEntryPoint = useCallback((entryPoint: EventEntryPointsEnum) => {
    lastUsedEntryPointRef.current = entryPoint;
  }, []);

  const trackLinkEvent = useCallback(() => {
    const entryPoint = lastUsedEntryPointRef.current;
    trackFormattingEvent(conversationId, CommentFormattingOptionsEnum.LINK, entryPoint);
  }, [conversationId]);

  return [setLastUsedEntryPoint, trackLinkEvent] as const;
}
